import { User } from '@/types';

export type NotificationPreferenceKey =
  | 'notiEmail'
  | 'notiPush'
  | 'notiEvents'
  | 'notiPayments'
  | 'notiIncidents'
  | 'notiMeetings'
  | 'notiAgreements'
  | 'notiNotices'
  | 'notiPolls'
  | 'notiProposals'
  | 'notiDocuments';

export type NotificationPreferences = Record<NotificationPreferenceKey, boolean>;

export interface NotificationPreferenceOption {
  key: NotificationPreferenceKey;
  label: string;
  description: string;
  disabled?: boolean;
  helperText?: string;
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  notiEmail: false,
  notiPush: false,
  notiEvents: true,
  notiPayments: false,
  notiIncidents: true,
  notiMeetings: true,
  notiAgreements: true,
  notiNotices: true,
  notiPolls: true,
  notiProposals: true,
  notiDocuments: true,
};

export const NOTIFICATION_FIELD_MAPPINGS: Record<NotificationPreferenceKey, `noti_${string}`> = {
  notiEmail: 'noti_email',
  notiPush: 'noti_push',
  notiEvents: 'noti_events',
  notiPayments: 'noti_payments',
  notiIncidents: 'noti_incidents',
  notiMeetings: 'noti_meetings',
  notiAgreements: 'noti_agreements',
  notiNotices: 'noti_notices',
  notiPolls: 'noti_polls',
  notiProposals: 'noti_proposals',
  notiDocuments: 'noti_documents',
};

export const NOTIFICATION_PREFERENCE_KEYS = Object.keys(DEFAULT_NOTIFICATION_PREFERENCES) as NotificationPreferenceKey[];

const RENTER_ALLOWED_CHANNEL_KEYS: NotificationPreferenceKey[] = ['notiEmail', 'notiPush'];
const RENTER_ALLOWED_TYPE_KEYS: NotificationPreferenceKey[] = ['notiEvents', 'notiNotices'];

export const NOTIFICATION_CHANNEL_OPTIONS: NotificationPreferenceOption[] = [
  {
    key: 'notiEmail',
    label: 'Notificaciones por email',
    description: 'Recibe alertas importantes directamente en tu correo electrónico.',
    disabled: true,
    helperText: 'Próximamente disponible.',
  },
  {
    key: 'notiPush',
    label: 'Notificaciones push',
    description: 'Activa avisos en tiempo real en tus dispositivos compatibles.',
  },
];

export const NOTIFICATION_TYPE_OPTIONS: NotificationPreferenceOption[] = [
  {
    key: 'notiEvents',
    label: 'Eventos y actividades',
    description: 'Entérate de nuevos eventos organizados en tu comunidad.',
  },
  {
    key: 'notiPayments',
    label: 'Pagos y recibos',
    description: 'Recibe recordatorios de pagos pendientes y confirmaciones de pago.',
  },
  {
    key: 'notiIncidents',
    label: 'Incidencias',
    description: 'Sigue las novedades sobre incidencias reportadas y su resolución.',
  },
  {
    key: 'notiMeetings',
    label: 'Juntas y reuniones',
    description: 'Obtén recordatorios y actas de próximas reuniones.',
  },
  {
    key: 'notiAgreements',
    label: 'Acuerdos',
    description: 'Mantente informado sobre acuerdos alcanzados por la comunidad.',
  },
  {
    key: 'notiNotices',
    label: 'Anuncios',
    description: 'Recibe avisos generales publicados por la administración.',
  },
  {
    key: 'notiPolls',
    label: 'Votaciones y encuestas',
    description: 'Participa en nuevas votaciones y conoce sus resultados.',
  },
  {
    key: 'notiProposals',
    label: 'Propuestas',
    description: 'Sigue las nuevas propuestas y su estado.',
  },
  {
    key: 'notiDocuments',
    label: 'Documentación',
    description:
      'Recibe avisos cuando se publiquen nuevos documentos en el apartado de documentación.',
  },
];

const ALL_NOTIFICATION_TYPE_KEYS = NOTIFICATION_TYPE_OPTIONS.map(
  option => option.key,
) as NotificationPreferenceKey[];

const filterTypeOptionsByRole = (
  options: NotificationPreferenceOption[],
  role?: string | null,
  allowedKeys?: NotificationPreferenceKey[]
): NotificationPreferenceOption[] => {
  if (role === 'admin') {
    return [];
  }
  if (role !== 'renter') {
    return options;
  }
  if (!allowedKeys || allowedKeys.length === 0) {
    return [];
  }
  return options.filter(option => allowedKeys.includes(option.key));
};

const filterChannelOptionsByRole = (
  options: NotificationPreferenceOption[],
  role?: string | null,
  allowedKeys?: NotificationPreferenceKey[]
): NotificationPreferenceOption[] => {
  if (role !== 'renter') {
    return options;
  }
  if (!allowedKeys || allowedKeys.length === 0) {
    return [];
  }
  return options.filter(option => allowedKeys.includes(option.key));
};

export const getNotificationChannelOptionsForRole = (role?: string | null) =>
  filterChannelOptionsByRole(NOTIFICATION_CHANNEL_OPTIONS, role, RENTER_ALLOWED_CHANNEL_KEYS);

export const getNotificationTypeOptionsForRole = (role?: string | null) =>
  filterTypeOptionsByRole(NOTIFICATION_TYPE_OPTIONS, role, RENTER_ALLOWED_TYPE_KEYS);

export const getNotificationTypePreferenceKeysForRole = (role?: string | null) => {
  if (role === 'admin') {
    return [];
  }
  return role === 'renter' ? RENTER_ALLOWED_TYPE_KEYS : ALL_NOTIFICATION_TYPE_KEYS;
};

export const resolveNotificationPreferences = (
  user?: Partial<User> | null
): NotificationPreferences => {
  const base: NotificationPreferences = { ...DEFAULT_NOTIFICATION_PREFERENCES };
  if (!user) {
    return base;
  }

  const record = user as Record<string, unknown>;
  for (const key of NOTIFICATION_PREFERENCE_KEYS) {
    const directValue = user[key];
    if (typeof directValue === 'boolean') {
      base[key] = directValue;
      continue;
    }
    const mappedKey = NOTIFICATION_FIELD_MAPPINGS[key];
    const rawValue = record[mappedKey];
    if (typeof rawValue === 'boolean') {
      base[key] = rawValue;
    }
  }
  return base;
};

export const diffNotificationPreferences = (
  previous: NotificationPreferences,
  next: NotificationPreferences
): Partial<NotificationPreferences> => {
  const changes: Partial<NotificationPreferences> = {};
  for (const key of NOTIFICATION_PREFERENCE_KEYS) {
    if (previous[key] !== next[key]) {
      changes[key] = next[key];
    }
  }
  return changes;
};
